(function () {
  'use strict';

  const state = {
    singleOriginalFile: null,
    singleProcessedFile: null,
    separateOriginalFiles: {},
    separateProcessedFiles: {},
    crop: null
  };

  function fn(name) {
    return typeof window[name] === 'function' ? window[name] : null;
  }

  function isValid(file) {
    const validator = fn('isValidImageFile');
    if (validator) return validator(file);
    return !!(file && file.type && file.type.indexOf('image/') === 0);
  }

  function isHeic(file) {
    const checker = fn('isHeicOrHeifUploadFile');
    if (checker) return checker(file);
    const name = (file && file.name ? file.name : '').toLowerCase();
    return /\.(heic|heif)$/.test(name);
  }

  function createUrl(file) {
    const helper = fn('createObjectUrl');
    return helper ? helper(file) : URL.createObjectURL(file);
  }

  function revokeUrl(url) {
    if (!url) return;
    const helper = fn('revokeObjectUrl');
    if (helper) helper(url);
    else URL.revokeObjectURL(url);
  }

  function showError(message) {
    const helper = fn('showSmsError');
    if (helper) helper(message);
    else window.alert(message);
  }

  function getActionElements(kind, index) {
    if (kind === 'separate') {
      return {
        help: document.getElementById('separateUploadHelpText_' + index),
        actions: document.getElementById('separatePhotoActions_' + index)
      };
    }

    return {
      help: document.getElementById('singleUploadHelpText'),
      actions: document.getElementById('singlePhotoActions')
    };
  }

  function updateActions(kind, index, hasPhoto) {
    const els = getActionElements(kind, index);
    if (els.help) els.help.style.display = hasPhoto ? 'none' : 'block';
    if (els.actions) els.actions.style.display = hasPhoto ? 'inline-flex' : 'none';
  }

  function getProcessedFile(kind, index) {
    if (kind === 'separate') {
      return state.separateProcessedFiles[index] || null;
    }

    return state.singleProcessedFile || null;
  }

  function setStoredFile(kind, index, file, options) {
    const opts = options || {};
    if (!file || !isValid(file)) return;

    if (kind === 'separate') {
      if (opts.original) state.separateOriginalFiles[index] = file;
      if (opts.processed) state.separateProcessedFiles[index] = file;
      updateActions('separate', index, true);
      return;
    }

    if (opts.original) state.singleOriginalFile = file;
    if (opts.processed) state.singleProcessedFile = file;
    updateActions('single', null, true);
  }

  function clearStoredFile(kind, index) {
    if (kind === 'separate') {
      delete state.separateOriginalFiles[index];
      delete state.separateProcessedFiles[index];
      updateActions('separate', index, false);
      return;
    }

    state.singleOriginalFile = null;
    state.singleProcessedFile = null;
    updateActions('single', null, false);
  }

  function getSingleOriginalCandidate() {
    if (state.singleOriginalFile && isValid(state.singleOriginalFile)) return state.singleOriginalFile;

    const input = document.getElementById('photoInput');
    if (input && input.files && input.files.length === 1 && isValid(input.files[0])) return input.files[0];

    const prefill = fn('getSinglePrefillUploadFile');
    if (prefill) {
      const file = prefill();
      if (file && isValid(file)) return file;
    }

    return null;
  }

  function getSeparateOriginalCandidate(index, title) {
    if (state.separateOriginalFiles[index] && isValid(state.separateOriginalFiles[index])) {
      return state.separateOriginalFiles[index];
    }

    const input = document.getElementById('separatePhotoInput_' + index);
    if (input && input.files && input.files.length > 0 && isValid(input.files[0])) return input.files[0];

    const prefill = fn('getSeparatePrefillUploadFile');
    if (prefill) {
      const file = prefill(index, title || ('Photo ' + (index + 1)));
      if (file && isValid(file)) return file;
    }

    return null;
  }

  function getCurrentActionFile(kind, index) {
    if (kind === 'separate') {
      const input = document.getElementById('separatePhotoInput_' + index);
      const title = input ? (input.getAttribute('data-title') || ('Photo ' + (index + 1))) : ('Photo ' + (index + 1));
      const current = fn('getCurrentSeparateUploadFile');
      return current ? current(input, index, title) : getSeparateOriginalCandidate(index, title);
    }

    const currentSingle = fn('getSingleFreshUploadFile');
    if (currentSingle) {
      const file = currentSingle();
      if (file && isValid(file)) return file;
    }

    return getSingleOriginalCandidate();
  }

  function editedFileName(file, suffix) {
    const rawName = file && file.name ? file.name : 'photo.jpg';
    const dotIndex = rawName.lastIndexOf('.');
    const base = dotIndex > 0 ? rawName.slice(0, dotIndex) : rawName;
    return base.replace(/[^a-zA-Z0-9_-]+/g, '-') + '-' + suffix + '.jpg';
  }

  function loadImageFromFile(file) {
    return new Promise(function (resolve, reject) {
      if (!file || isHeic(file)) {
        reject(new Error('This image format cannot be edited in the browser. Please use JPG, PNG, or WEBP, or choose a new photo.'));
        return;
      }

      const url = createUrl(file);
      const img = new Image();

      img.onload = function () {
        revokeUrl(url);
        resolve(img);
      };

      img.onerror = function () {
        revokeUrl(url);
        reject(new Error('Could not load this photo for editing. Please try a different image.'));
      };

      img.src = url;
    });
  }

  function canvasToJpegFile(canvas, name) {
    return new Promise(function (resolve, reject) {
      if (!canvas || !canvas.toBlob) {
        reject(new Error('This browser cannot process the photo.'));
        return;
      }

      canvas.toBlob(function (blob) {
        if (!blob) {
          reject(new Error('Could not save the edited photo.'));
          return;
        }

        resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', 0.92);
    });
  }

  async function rotateFile(file) {
    const img = await loadImageFromFile(file);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalHeight || img.height;
    canvas.height = img.naturalWidth || img.width;

    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -canvas.height / 2, -canvas.width / 2, canvas.height, canvas.width);

    return canvasToJpegFile(canvas, editedFileName(file, 'rotated'));
  }

  function renderProcessedFile(kind, index, file) {
    if (kind === 'separate') {
      const input = document.getElementById('separatePhotoInput_' + index);
      const title = input ? (input.getAttribute('data-title') || ('Photo ' + (index + 1))) : ('Photo ' + (index + 1));
      const save = fn('saveSmsPhotoPrefillSeparate');
      const render = fn('renderSeparatePhotoFile');
      const ready = fn('updateSeparateReadyState');
      const badge = fn('updateStepBadgeForCurrentMode');

      state.separateProcessedFiles[index] = file;
      if (!state.separateOriginalFiles[index]) state.separateOriginalFiles[index] = file;
      if (save) save(index, title, file);
      if (render) render(file, index, 'separatePhotoPreview_' + index, 'separatePhotoIcon_' + index, 'separatePhotoBox_' + index);
      if (ready) ready();
      if (badge) badge();
      updateActions('separate', index, true);
      return;
    }

    const save = fn('saveSmsPhotoPrefillSingle');
    const render = fn('renderSinglePhoto');
    const ready = fn('updateSingleReadyState');
    const badge = fn('updateStepBadgeForCurrentMode');

    state.singleProcessedFile = file;
    if (!state.singleOriginalFile) state.singleOriginalFile = file;
    if (save) save(file);
    if (render) render(file);
    if (ready) ready();
    if (badge) badge();
    updateActions('single', null, true);
  }

  function resetFile(kind, index) {
    if (kind === 'separate') {
      const input = document.getElementById('separatePhotoInput_' + index);
      const title = input ? (input.getAttribute('data-title') || ('Photo ' + (index + 1))) : ('Photo ' + (index + 1));
      const original = getSeparateOriginalCandidate(index, title);
      if (!original) return;
      renderProcessedFile('separate', index, original);
      return;
    }

    const originalSingle = getSingleOriginalCandidate();
    if (!originalSingle) return;
    renderProcessedFile('single', null, originalSingle);
  }

  async function handleAction(action, kind, index) {
    try {
      if (action === 'change') {
        const input = kind === 'separate' ? document.getElementById('separatePhotoInput_' + index) : document.getElementById('photoInput');
        if (input) input.click();
        return;
      }

      if (action === 'reset') {
        resetFile(kind, index);
        return;
      }

      const file = getCurrentActionFile(kind, index);
      if (!file || !isValid(file)) return;

      if (action === 'rotate') {
        const rotatedFile = await rotateFile(file);
        renderProcessedFile(kind, index, rotatedFile);
        return;
      }

      if (action === 'crop') {
        await openCropTool(kind, index, file);
      }
    } catch (e) {
      showError(e && e.message ? e.message : 'Could not edit this photo. Please try a different image.');
    }
  }

  let smsCropBodyLock = null;

  function lockSmsCropBodyScroll() {
    if (smsCropBodyLock || !document.body) return;

    smsCropBodyLock = {
      overflow: document.body.style.overflow,
      overscrollBehavior: document.body.style.overscrollBehavior,
      touchAction: document.body.style.touchAction
    };

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
  }

  function unlockSmsCropBodyScroll() {
    if (!smsCropBodyLock || !document.body) return;

    document.body.style.overflow = smsCropBodyLock.overflow || '';
    document.body.style.overscrollBehavior = smsCropBodyLock.overscrollBehavior || '';
    document.body.style.touchAction = smsCropBodyLock.touchAction || '';
    smsCropBodyLock = null;
  }

  function preventSmsCropTouchDefault(event) {
    if (!state.crop) return;
    if (event && event.cancelable) {
      event.preventDefault();
    }
  }

  function closeCropTool() {
    const modal = document.getElementById('smsCropModal');
    const img = document.getElementById('smsCropImage');

    if (state.crop && state.crop.objectUrl) revokeUrl(state.crop.objectUrl);
    state.crop = null;

    if (img) {
      img.src = '';
      img.style.transform = '';
      img.style.width = '';
      img.style.height = '';
    }

    if (modal) {
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
    }

    unlockSmsCropBodyScroll();
  }

  function updateCropTransform() {
    if (!state.crop) return;
    const img = document.getElementById('smsCropImage');
    if (!img) return;

    const displayW = state.crop.naturalWidth * state.crop.baseScale * state.crop.zoom;
    const displayH = state.crop.naturalHeight * state.crop.baseScale * state.crop.zoom;
    const minCenterX = state.crop.frameWidth - displayW / 2;
    const maxCenterX = displayW / 2;
    const minCenterY = state.crop.frameHeight - displayH / 2;
    const maxCenterY = displayH / 2;

    if (displayW >= state.crop.frameWidth) {
      state.crop.centerX = Math.min(maxCenterX, Math.max(minCenterX, state.crop.centerX));
    } else {
      state.crop.centerX = state.crop.frameWidth / 2;
    }

    if (displayH >= state.crop.frameHeight) {
      state.crop.centerY = Math.min(maxCenterY, Math.max(minCenterY, state.crop.centerY));
    } else {
      state.crop.centerY = state.crop.frameHeight / 2;
    }

    img.style.width = displayW + 'px';
    img.style.height = displayH + 'px';
    img.style.transform = 'translate(' + (state.crop.centerX - displayW / 2) + 'px, ' + (state.crop.centerY - displayH / 2) + 'px)';
  }

  async function openCropTool(kind, index, file) {
    await loadImageFromFile(file);

    const modal = document.getElementById('smsCropModal');
    const frame = document.getElementById('smsCropFrame');
    const img = document.getElementById('smsCropImage');
    const zoom = document.getElementById('smsCropZoomRange');
    if (!modal || !frame || !img || !zoom) return;

    const objectUrl = createUrl(file);
    lockSmsCropBodyScroll();
    modal.style.display = 'flex';
    modal.setAttribute('aria-hidden', 'false');
    zoom.value = '1';

    await new Promise(function (resolve, reject) {
      img.onload = function () { resolve(); };
      img.onerror = function () { reject(new Error('Could not open this photo for cropping.')); };
      img.src = objectUrl;
    });

    const frameRect = frame.getBoundingClientRect();
    const naturalW = img.naturalWidth || img.width;
    const naturalH = img.naturalHeight || img.height;
    const baseScale = Math.max(frameRect.width / naturalW, frameRect.height / naturalH);

    state.crop = {
      kind: kind,
      index: index,
      file: file,
      objectUrl: objectUrl,
      naturalWidth: naturalW,
      naturalHeight: naturalH,
      frameWidth: frameRect.width,
      frameHeight: frameRect.height,
      baseScale: baseScale,
      zoom: 1,
      centerX: frameRect.width / 2,
      centerY: frameRect.height / 2,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startCenterX: 0,
      startCenterY: 0
    };

    updateCropTransform();
  }

  async function applyCropTool() {
    if (!state.crop) return;

    const img = document.getElementById('smsCropImage');
    if (!img) return;

    const scale = state.crop.baseScale * state.crop.zoom;
    const sourceX = Math.max(0, (0 - (state.crop.centerX - (state.crop.naturalWidth * scale) / 2)) / scale);
    const sourceY = Math.max(0, (0 - (state.crop.centerY - (state.crop.naturalHeight * scale) / 2)) / scale);
    const sourceW = Math.min(state.crop.naturalWidth - sourceX, state.crop.frameWidth / scale);
    const sourceH = Math.min(state.crop.naturalHeight - sourceY, state.crop.frameHeight / scale);
    // Preserve the exact source-crop aspect ratio. The rendered crop frame can
    // become non-square when its CSS max-height clamps it on wider screens, so
    // forcing every result to 1400x1400 stretches the customer's photo.
    const maxOutputSize = 1400;
    const sourceAspect = sourceW / sourceH;
    const outputWidth = sourceAspect >= 1
      ? maxOutputSize
      : Math.max(1, Math.round(maxOutputSize * sourceAspect));
    const outputHeight = sourceAspect >= 1
      ? Math.max(1, Math.round(maxOutputSize / sourceAspect))
      : maxOutputSize;
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputWidth, outputHeight);
    ctx.drawImage(img, sourceX, sourceY, sourceW, sourceH, 0, 0, outputWidth, outputHeight);

    const croppedFile = await canvasToJpegFile(canvas, editedFileName(state.crop.file, 'cropped'));
    const kind = state.crop.kind;
    const index = state.crop.index;

    closeCropTool();
    renderProcessedFile(kind, index, croppedFile);
  }

  function bindCropEvents() {
    const frame = document.getElementById('smsCropFrame');
    const zoom = document.getElementById('smsCropZoomRange');
    const closeBtn = document.getElementById('smsCropCloseBtn');
    const cancelBtn = document.getElementById('smsCropCancelBtn');
    const applyBtn = document.getElementById('smsCropApplyBtn');

    if (closeBtn) closeBtn.addEventListener('click', closeCropTool);
    if (cancelBtn) cancelBtn.addEventListener('click', closeCropTool);

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        applyCropTool().catch(function (e) {
          showError(e && e.message ? e.message : 'Could not crop this photo. Please try again.');
        });
      });
    }

    if (zoom) {
      zoom.addEventListener('input', function () {
        if (!state.crop) return;
        state.crop.zoom = parseFloat(zoom.value || '1') || 1;
        updateCropTransform();
      });
    }

    if (!frame) return;

    frame.addEventListener('pointerdown', function (event) {
      preventSmsCropTouchDefault(event);
      if (!state.crop) return;
      state.crop.dragging = true;
      state.crop.dragStartX = event.clientX;
      state.crop.dragStartY = event.clientY;
      state.crop.startCenterX = state.crop.centerX;
      state.crop.startCenterY = state.crop.centerY;
      try { frame.setPointerCapture(event.pointerId); } catch (e) {}
    });

    frame.addEventListener('pointermove', function (event) {
      preventSmsCropTouchDefault(event);
      if (!state.crop || !state.crop.dragging) return;
      state.crop.centerX = state.crop.startCenterX + (event.clientX - state.crop.dragStartX);
      state.crop.centerY = state.crop.startCenterY + (event.clientY - state.crop.dragStartY);
      updateCropTransform();
    });

    frame.addEventListener('pointerup', function (event) {
      if (!state.crop) return;
      state.crop.dragging = false;
      try { frame.releasePointerCapture(event.pointerId); } catch (e) {}
    });

    frame.addEventListener('pointercancel', function () {
      if (state.crop) state.crop.dragging = false;
    });

    frame.addEventListener('touchstart', preventSmsCropTouchDefault, { passive: false });
    frame.addEventListener('touchmove', preventSmsCropTouchDefault, { passive: false });
  }

  function bindUploadActions() {
    document.addEventListener('click', function (event) {
      const action = event.target && event.target.closest ? event.target.closest('.sms-upload-action-link') : null;
      if (!action) return;

      event.preventDefault();
      event.stopPropagation();

      const kind = action.getAttribute('data-upload-kind') || 'single';
      const indexAttr = action.getAttribute('data-upload-index');
      const index = indexAttr === null || indexAttr === '' ? null : parseInt(indexAttr, 10);
      handleAction(action.getAttribute('data-sms-photo-action'), kind, index);
    });
  }

  window.SMSUploadPhotoTools = {
    getProcessedFile: getProcessedFile,
    setStoredFile: setStoredFile,
    clearStoredFile: clearStoredFile,
    updateActions: updateActions,
    closeCropTool: closeCropTool
  };

  bindUploadActions();
  bindCropEvents();
})();
